'use client'

import { memo } from 'react'
import { Chip } from '@heroui/react'
import { Sidebar } from '@heroui-pro/react'
import { CircleHelp, LogOut, ShieldCheck } from 'lucide-react'
import { APP_NAME } from '../lib/branding'
import { navItems, isCurrentNavItem } from './navigation'

interface AppSidebarProps {
  pathname: string
  onNavigate: (path: string) => void
  onLogout: () => void
}

export const AppSidebarContent = memo(function AppSidebarContent({
  pathname,
  onNavigate,
  onLogout,
}: AppSidebarProps): React.ReactElement {
  return (
    <>
      <Sidebar.Header>
        <div className="flex items-center gap-3 px-1 py-2">
          <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
            <ShieldCheck className="size-3.5" />
          </div>
          <span className="truncate text-sm font-semibold text-foreground" data-sidebar="label">
            {APP_NAME}
          </span>
        </div>
      </Sidebar.Header>

      <Sidebar.Content>
        <Sidebar.Group>
          {/*
            Custom menu (not Sidebar.MenuItem): HeroUI only navigates when AppLayout's
            NavigateContext is present. The mobile drawer is outside that context, so
            items only got hover styles. Own onClick handlers work in both places.
          */}
          <ul className="sidebar-menu" role="menu" aria-label="Workspace navigation">
            {navItems.map((item) => {
              const Icon = item.icon
              const isCurrent = !item.disabled && isCurrentNavItem(pathname, item)

              return (
                <li
                  key={item.id}
                  role="menuitem"
                  title={item.label}
                  className={`sidebar-menu-item ${isCurrent ? 'current' : ''}`}
                  data-disabled={item.disabled || undefined}
                  aria-current={isCurrent ? 'page' : undefined}
                  aria-disabled={item.disabled || undefined}
                  tabIndex={item.disabled ? -1 : 0}
                  onClick={() => {
                    if (item.disabled || !item.to) return
                    onNavigate(item.to)
                  }}
                  onKeyDown={(event) => {
                    if (item.disabled || !item.to) return
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onNavigate(item.to)
                    }
                  }}
                >
                  <span className="sidebar-menu-icon">
                    <Icon className="size-4" />
                  </span>
                  <span className="sidebar-menu-label">{item.label}</span>
                  {(item.disabled || item.badge) && (
                    <span className="sidebar-menu-chip">
                      <Chip color={item.badge ? 'success' : 'default'} size="sm" variant="soft">
                        {item.badge ?? 'Soon'}
                      </Chip>
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </Sidebar.Group>
      </Sidebar.Content>

      <Sidebar.Footer>
        <ul className="sidebar-menu" role="menu" aria-label="Footer actions">
          <li
            role="menuitem"
            title="Help & Information"
            className="sidebar-menu-item"
            data-disabled="true"
            aria-disabled="true"
            tabIndex={-1}
          >
            <span className="sidebar-menu-icon">
              <CircleHelp className="size-4" />
            </span>
            <span className="sidebar-menu-label">Help & Information</span>
          </li>
          <li
            role="menuitem"
            title="Sign Out"
            className="sidebar-menu-item"
            tabIndex={0}
            onClick={onLogout}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onLogout()
              }
            }}
          >
            <span className="sidebar-menu-icon">
              <LogOut className="size-4" />
            </span>
            <span className="sidebar-menu-label">Sign Out</span>
          </li>
        </ul>
      </Sidebar.Footer>
    </>
  )
})
